import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownWideNarrow,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Clock,
  ListChecks,
  ShieldCheck,
} from 'lucide-react';
import type { PersonKpiRow } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const peopleKpiRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people/kpi',
  component: PeopleKpiPage,
});

type SortKey = 'name' | 'hours' | 'missed' | 'overdue' | 'done' | 'billable';

const SORTS: Record<
  SortKey,
  { label: string; compare: (a: PersonKpiRow, b: PersonKpiRow) => number }
> = {
  name: {
    label: 'Name',
    compare: (a, b) => (a.personName ?? a.personId).localeCompare(b.personName ?? b.personId),
  },
  hours: { label: 'Hours clocked', compare: (a, b) => b.workedMinutes - a.workedMinutes },
  missed: { label: 'Missed clock-ins', compare: (a, b) => b.absentDays - a.absentDays },
  overdue: { label: 'Overdue tasks', compare: (a, b) => b.overdueTasks - a.overdueTasks },
  done: { label: 'Tasks done', compare: (a, b) => b.completedTasks - a.completedTasks },
  billable: { label: 'Billable share', compare: (a, b) => b.billablePct - a.billablePct },
};

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y!, m! - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function hours(minutes: number): string {
  if (minutes === 0) return '0';
  const h = minutes / 60;
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

function daysX10(x10: number): string {
  const whole = Math.trunc(x10 / 10);
  const frac = Math.abs(x10 % 10);
  return frac === 0 ? String(whole) : `${whole}.${frac}`;
}

function PeopleKpiPage(): JSX.Element {
  const [month, setMonth] = React.useState(currentMonth());
  const [sort, setSort] = React.useState<SortKey>('name');
  const kpiQuery = useQuery({
    queryKey: ['people-kpi', month],
    queryFn: () => api.getPeopleKpi(month),
  });
  const rows = React.useMemo(
    () => [...(kpiQuery.data?.rows ?? [])].sort(SORTS[sort].compare),
    [kpiQuery.data, sort],
  );

  const team = React.useMemo(() => {
    const all = kpiQuery.data?.rows ?? [];
    const presentDays = all.reduce((n, r) => n + r.presentDays, 0);
    const expectedDays = all.reduce(
      (n, r) => n + r.presentDays + r.absentDays + r.excusedDays + r.lopDays,
      0,
    );
    return {
      workedMinutes: all.reduce((n, r) => n + r.workedMinutes, 0),
      attendancePct: expectedDays === 0 ? null : Math.round((presentDays / expectedDays) * 100),
      overdueTasks: all.reduce((n, r) => n + r.overdueTasks, 0),
      missedClockIns: all.reduce((n, r) => n + r.absentDays, 0),
      completedTasks: all.reduce((n, r) => n + r.completedTasks, 0),
      peopleWithIssues: all.filter((r) => r.absentDays > 0 || r.overdueTasks > 0).length,
    };
  }, [kpiQuery.data]);

  const atCurrentMonth = month >= currentMonth();

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
            Team KPIs
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">
            Each person&rsquo;s month in one card: attendance, hours clocked, leave, task throughput
            and how logged time splits between client and internal work, from the same records the
            operational screens show.
          </p>
          <p className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
            <ShieldCheck className="size-3.5" />
            Admin only. Nobody else can see these numbers, including their own.
          </p>
        </div>

        <div className="flex items-center gap-1 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Previous month"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="min-w-36 text-center text-sm font-medium tabular-nums">
            {monthLabel(month)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Next month"
            disabled={atCurrentMonth}
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      {kpiQuery.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load</AlertTitle>
          <AlertDescription>{toMessage(kpiQuery.error)}</AlertDescription>
        </Alert>
      ) : null}

      {kpiQuery.data ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Tile
            icon={Clock}
            label="Hours clocked"
            value={hours(team.workedMinutes)}
            hint="across the team this month"
          />
          <Tile
            icon={CalendarCheck}
            label="Attendance"
            value={team.attendancePct === null ? '—' : `${team.attendancePct}%`}
            hint="present days over expected days"
          />
          <Tile
            icon={ListChecks}
            label="Tasks done"
            value={String(team.completedTasks)}
            hint={
              team.overdueTasks === 0 ? 'nothing overdue' : `${team.overdueTasks} open and overdue`
            }
            tone={team.overdueTasks > 0 ? 'warn' : 'neutral'}
          />
          <Tile
            icon={AlertTriangle}
            label="Needs attention"
            value={String(team.peopleWithIssues)}
            hint={
              team.peopleWithIssues === 0
                ? 'no missed days or overdue work'
                : `${team.missedClockIns} missed clock-ins to rule on`
            }
            tone={team.peopleWithIssues > 0 ? 'warn' : 'neutral'}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold">
          {kpiQuery.isPending
            ? 'Loading…'
            : `${rows.length} active ${rows.length === 1 ? 'person' : 'people'}`}
        </h2>
        <div className="flex items-center gap-2">
          <Label htmlFor="kpi-sort" className="inline-flex items-center gap-1.5 text-xs">
            <ArrowDownWideNarrow className="size-3.5" />
            Sort by
          </Label>
          <Select
            id="kpi-sort"
            className="h-9 w-44"
            value={sort}
            onChange={(e) => setSort(e.currentTarget.value as SortKey)}
          >
            {(Object.keys(SORTS) as SortKey[]).map((k) => (
              <option key={k} value={k}>
                {SORTS[k].label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {kpiQuery.isPending ? null : rows.length === 0 ? (
        <div className="rounded-[18px] border border-dashed border-[hsl(var(--border))] py-14 text-center">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Nobody was active in {monthLabel(month)}.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {rows.map((r) => (
            <PersonCard key={r.personId} row={r} />
          ))}
        </div>
      )}
    </AppShell>
  );
}

const ATTENDANCE_SEGMENTS: {
  key: 'presentDays' | 'leaveDays' | 'excusedDays' | 'lopDays' | 'absentDays';
  label: string;
  color: string;
}[] = [
  { key: 'presentDays', label: 'Present', color: 'bg-[hsl(var(--brand))]' },
  { key: 'leaveDays', label: 'Leave', color: 'bg-violet-400' },
  { key: 'excusedDays', label: 'Excused', color: 'bg-sky-400' },
  { key: 'lopDays', label: 'LOP', color: 'bg-orange-400' },
  { key: 'absentDays', label: 'Missed', color: 'bg-rose-500' },
];

function PersonCard({ row }: { row: PersonKpiRow }): JSX.Element {
  const leaveDays = row.leaveDaysX10 / 10;
  const counts = {
    presentDays: row.presentDays,
    leaveDays,
    excusedDays: row.excusedDays,
    lopDays: row.lopDays,
    absentDays: row.absentDays,
  };
  const total = Object.values(counts).reduce((n, v) => n + v, 0);
  const needsAttention = row.absentDays > 0 || row.overdueTasks > 0;
  const onTimePct =
    row.completedTasks === 0 ? null : Math.round((row.completedOnTime / row.completedTasks) * 100);

  return (
    <article
      className={cn(
        'flex flex-col gap-5 rounded-[18px] border bg-[hsl(var(--card))] p-6 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.10)]',
        needsAttention ? 'border-rose-200' : 'border-[hsl(var(--border))]/60',
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-lg font-semibold leading-tight">
            {row.personName ?? row.personId}
          </h3>
          {row.employeeId ? (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">{row.employeeId}</p>
          ) : null}
        </div>
        <Badge variant={row.billablePct > 0 ? 'secondary' : 'muted'} className="shrink-0">
          {row.billablePct}% billable
        </Badge>
      </header>

      <section className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between text-xs text-[hsl(var(--muted-foreground))]">
          <span className="uppercase tracking-wide">Attendance</span>
          <span className="tabular-nums">
            {row.presentDays} of {daysX10(Math.round(total * 10))} days present
          </span>
        </div>
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-[hsl(var(--muted))]">
          {total > 0
            ? ATTENDANCE_SEGMENTS.filter((s) => counts[s.key] > 0).map((s) => (
                <div
                  key={s.key}
                  className={s.color}
                  style={{ width: `${(counts[s.key] / total) * 100}%` }}
                  title={`${s.label}: ${daysX10(Math.round(counts[s.key] * 10))}`}
                />
              ))
            : null}
        </div>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {ATTENDANCE_SEGMENTS.map((s) => (
            <li
              key={s.key}
              className={cn(
                'inline-flex items-center gap-1.5',
                counts[s.key] === 0
                  ? 'text-[hsl(var(--muted-foreground))]/60'
                  : 'text-[hsl(var(--foreground))]',
                s.key === 'absentDays' && counts[s.key] > 0 && 'font-medium text-rose-600',
              )}
            >
              <span className={cn('size-2 rounded-full', s.color)} />
              {s.label}
              <span className="tabular-nums">{daysX10(Math.round(counts[s.key] * 10))}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="grid grid-cols-2 gap-x-6 gap-y-4">
        <Stat
          label="Hours clocked"
          value={hours(row.workedMinutes)}
          unit="h"
          hint={
            row.presentDays === 0
              ? 'no days clocked'
              : `${hours(row.avgMinutesPerPresentDay)} h per day`
          }
        />
        <Stat
          label="Tasks done"
          value={String(row.completedTasks)}
          hint={
            onTimePct === null
              ? 'none finished this month'
              : `${row.completedOnTime} on time · ${onTimePct}%`
          }
        />
        <Stat
          label="Open tasks"
          value={String(row.openTasks)}
          hint={row.overdueTasks === 0 ? 'nothing overdue' : `${row.overdueTasks} overdue`}
          tone={row.overdueTasks > 0 ? 'warn' : 'neutral'}
        />
        <Stat
          label="Billable hours"
          value={hours(row.billableMinutes)}
          unit="h"
          hint={
            row.billableMinutes + row.nonBillableMinutes + row.unmappedMinutes === 0
              ? 'no time logged'
              : row.unmappedMinutes > 0
                ? `${row.billablePct}% of logged time · ${hours(row.unmappedMinutes)} h unmapped`
                : `${row.billablePct}% of logged time`
          }
          tone={row.unmappedMinutes > 0 ? 'muted' : 'neutral'}
        />
      </section>
    </article>
  );
}

function Stat({
  label,
  value,
  unit,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  unit?: string;
  hint: string;
  tone?: 'neutral' | 'warn' | 'muted';
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
        {label}
      </span>
      <span className="font-display text-2xl font-semibold leading-none tabular-nums">
        {value}
        {unit ? (
          <span className="ml-0.5 text-sm font-normal text-[hsl(var(--muted-foreground))]">
            {unit}
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          'text-xs',
          tone === 'warn' ? 'font-medium text-rose-600' : 'text-[hsl(var(--muted-foreground))]',
        )}
      >
        {hint}
      </span>
    </div>
  );
}

function Tile({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint: string;
  tone?: 'neutral' | 'warn';
}): JSX.Element {
  return (
    <div className="flex items-start gap-4 rounded-[18px] border border-[hsl(var(--border))]/60 bg-[hsl(var(--card))] px-5 py-4">
      <span
        className={cn(
          'mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-xl',
          tone === 'warn'
            ? 'bg-rose-50 text-rose-600'
            : 'bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]',
        )}
      >
        <Icon className="size-4" />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
          {label}
        </span>
        <span
          className={cn(
            'font-display text-2xl font-semibold leading-none tabular-nums',
            tone === 'warn' && 'text-rose-600',
          )}
        >
          {value}
        </span>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{hint}</span>
      </div>
    </div>
  );
}
